import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { form, minLength, required, FormField, disabled } from '@angular/forms/signals';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { firstValueFrom, Subscription, take } from 'rxjs';
import { S3uploaderService } from '../../services/s3uploader/s3uploader.service';
import { ImageTypesEnum, S3BucketEnum } from '../../common/general-types/media-assets.dto';
import { UserService } from '../../services/user/user.service';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { ImageService } from '../../services/image/image.service';
import { NotificationTypeEnum } from '../../common/general-types/notifications.dto';
import { LoadingOverlayComponent } from '../../atoms/loading-overlay/loading-overlay.component';
import { AlertService } from '../../atoms/alert/alert.service';
import { ThemeService } from '../../services/theme/theme.service';
import { SHOUT_INTERNAL_ACCESS_ROLES } from '../../common/general-types/users.dto';
import { ToastNotificationService } from '../../atoms/toast-notification/toast-notification.service';


@Component({
  selector: 'app-my-account',
  standalone: true,
  imports: [FormField, LoadingOverlayComponent],
  templateUrl: './my-account.component.html',
  styleUrl: './my-account.component.css'
})
export class MyAccountComponent implements OnInit, OnDestroy {

  personalInformationModel = signal({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    mobileVerified: false,
    jobPosition: '',
  })
  readonly emailLocked = signal(true);
  personalInformationForm = form(this.personalInformationModel, (schemaPath) => {
    required(schemaPath.firstName, { message: 'First name is required' });
    minLength(schemaPath.firstName, 2, { message: 'First name must be at least 2 characters long' });
    required(schemaPath.lastName, { message: 'Last name is required' });
    minLength(schemaPath.lastName, 2, { message: 'Last name must be at least 2 characters long' });
    required(schemaPath.jobPosition, { message: 'Job position is required' });
    minLength(schemaPath.jobPosition, 2, { message: 'Job position must be at least 2 characters long' });
    disabled(schemaPath.email, () => this.emailLocked());
  })
  personalInformationUpdated = signal<boolean>(false);
  private isInitialized = false;

  /** Selected Profile Pictures */
  selectedFiles: any[] = [];
  profilePhoto = signal<any>('');
  imageLoading: boolean = false;

  S3Bucket = S3BucketEnum;
  ImageTypes = ImageTypesEnum;


  unSubscribedEmail: NotificationTypeEnum[] = [];
  notifications: { title: string, description: string, type: NotificationTypeEnum, checked: boolean }[] = [
    { title: 'New Applications', description: `Notified when creators apply to your briefs. You'll get an email when you reach 1, 5, 20, 50, and 100 applicants.`, type: NotificationTypeEnum.Applied, checked: false },
    { title: 'New Messages', description: `Notified when a creator messages you — available when you're assigned as a campaign manager.`, type: NotificationTypeEnum.NewMessage, checked: false },
    { title: 'Content Uploads', description: `Notified when a creator uploads content for one of your briefs — available when you're assigned as a campaign manager.`, type: NotificationTypeEnum.VideoUploaded, checked: false }
  ];
  showNotificationPreferences = signal<boolean>(false);
  notificationPreferencesUpdated = signal<boolean>(false);

  updatingNotificationPreferences = signal<boolean>(false);
  updatingPersonalInformation = signal<boolean>(false);
  emailSavingState = signal(false);
  passwordResetSendingState = signal(false);
  emailJustUpdated = signal(false);
  savedEmail = signal('');

  verifySending = signal<boolean>(false);
  subscriptionList = new Subscription();

  public currentUserService = inject(CurrentUserService);
  private userService = inject(UserService);
  private uploadService = inject(S3uploaderService);
  private firebaseService = inject(FirebaseService);
  public imageService = inject(ImageService);
  private alertService = inject(AlertService);
  public themeService = inject(ThemeService);
  private toastNotificationService = inject(ToastNotificationService);

  readonly canEditOwnEmail = computed(() => !this.emailLocked());

  readonly emailHasChanges = computed(() => {
    if (!this.canEditOwnEmail()) {
      return false;
    }
    this.personalInformationModel();
    const current = (this.personalInformationModel().email ?? '').trim().toLowerCase();
    const baseline = (this.savedEmail() ?? '').trim().toLowerCase();
    return current !== baseline && current.length > 0;
  });

  readonly canSaveEmail = computed(() => this.emailHasChanges() && !this.emailSavingState());
  readonly canSendPasswordReset = computed(
    () => this.emailJustUpdated() && !this.passwordResetSendingState() && !this.emailSavingState(),
  );

  constructor() {
    effect(() => {
      const model = this.personalInformationModel();
      if (this.isInitialized) {
        this.personalInformationUpdated.set(true);
      }
    });
  }

  async ngOnInit() {
    const user = await firstValueFrom(this.currentUserService.getShoutUser());

    console.log('----> user -> ', user);

    this.personalInformationModel.set({
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      mobile: user?.mobileNumber ?? '',
      jobPosition: user?.jobPosition ?? '',
      mobileVerified: user?.mobileVerified ?? false,
    })
    this.savedEmail.set(user?.email ?? '');
    this.emailLocked.set(!this.currentUserService.hasAnyRole(SHOUT_INTERNAL_ACCESS_ROLES));
    this.profilePhoto.set(user?.photoURL ?? '');

    // Mark as initialized after setting initial values
    this.isInitialized = true;

    this.getNotificationPreferences();
  }

  getNotificationPreferences() {
    this.userService.getNotificationPreferences(this.firebaseService.userId!).subscribe((preferences: any) => {
      console.log('Notification preferences:', preferences);
      for (let i = 0; i < this.notifications.length; i++) {
        this.notifications[i].checked = !preferences.unSubscribedEmail.includes(this.notifications[i].type);
      }
      this.unSubscribedEmail = preferences.unSubscribedEmail;
      this.showNotificationPreferences.set(true);
    });
  }

  toggleNotification(index: number) {
    this.notifications[index].checked = !this.notifications[index].checked;
    this.notificationPreferencesUpdated.set(true);
    // this.save();
  }

  selectFile(event: any): void {
    this.selectedFiles = event.target.files;
    this.upload(this.selectedFiles);
  }

  async upload(selectedFiles: any) {
    this.imageLoading = true;
    const uploadedFile: { file: string, fileKey: string } | null = await this.uploadService.uploadFile(selectedFiles, this.S3Bucket.PROFILE_PHOTOS, this.ImageTypes.GALLERY);

    if (uploadedFile) {
      this.profilePhoto.set(uploadedFile.fileKey);
      try {
        const user_updated: any = await firstValueFrom(this.userService.updateUser({ photoURL: this.profilePhoto() }, this.firebaseService.userId!));
        this.profilePhoto.set(user_updated.photoURL);
        this.currentUserService.setShoutUser(user_updated);
      } catch (error) {
        console.error('Error updating user:', error);
      }

    } else {
      console.error('No uploaded file');
    }
    this.imageLoading = false;
  }

  async saveChanges() {
    console.log('saveChanges()');


    let profileTouched = this.personalInformationForm.firstName().touched() || this.personalInformationForm.lastName().touched() || this.personalInformationForm.jobPosition().touched() || this.personalInformationForm.mobile().touched();

    if (profileTouched && this.personalInformationUpdated()) {
      console.log('Saving personal information');
      this.updatingPersonalInformation.set(true);
      this.userService.updateUser({
        firstName: this.personalInformationModel().firstName,
        lastName: this.personalInformationModel().lastName,
        jobPosition: this.personalInformationModel().jobPosition,
        mobileVerified: this.personalInformationModel().mobileVerified,
        mobileNumber: this.personalInformationModel().mobile,
      }, this.firebaseService.userId!).pipe(take(1)).subscribe((user: any) => {
        console.log('User updated:', user);
        this.currentUserService.setShoutUser(user);
        setTimeout(() => {
          this.updatingPersonalInformation.set(false);
        }, 500);
      });
    }

    if (this.notificationPreferencesUpdated()) {
      console.log('Saving notification preferences');
      this.updatingNotificationPreferences.set(true);
      this.userService.updateNotificationPreferences(this.firebaseService.userId!, {
        unSubscribedEmail: this.notifications.filter((notification) => !notification.checked).map((notification) => notification.type),
      }).pipe(take(1)).subscribe((preferences: any) => {
        console.log('Notification preferences updated:', preferences);
        setTimeout(() => {
          this.updatingNotificationPreferences.set(false);
        }, 500);
      });
    }
  }

  async verifyMobile() {
    console.log('----> verifyMobile() -> ');
    console.log('userData: ', this.currentUserService.shoutUser.value?.mobile);
    console.log('inupt value: ', this.personalInformationModel().mobile);

    this.verifySending.set(true);

    const mobile = this.personalInformationModel().mobile;
    console.log('mobile: ', mobile);
    // this.currentUserService.shoutUser.value!.mobile = mobile;
    // this.personalInformationModel.set({
    //   ...this.personalInformationModel(),
    //   mobile: mobile,
    // });
    console.log('personalInfoForm: ', this.personalInformationModel());
    /**
      * Validate Mobile number
      */

    if (mobile) {
      const verifySent = await firstValueFrom(this.userService.sendVerificationMessage(mobile));
      console.log('----> verifySent -> ', verifySent);
      if (verifySent) {

        this.alertService.prompt({
          title: 'Enter Verification Code',
          message: 'Verification code sent to mobile number.',
          icon: 'info',
          confirmText: 'Verify',
          cancelText: 'Cancel',
          type: 'confirm',
          showInput: true,
          inputLabel: 'Verification code',
          inputPlaceholder: 'Enter 4 digit code',
        }).then((result: any) => {
          console.log('----> result -> ', result);

          const verificationCode = result.value;
          console.log('----> verificationCode -> ', verificationCode);
          // console.log('----> verifySent.code -> ', verifySent.code);

          if (verificationCode === verifySent.code.toString()) {
            this.personalInformationUpdated.set(true);
            this.personalInformationModel().mobileVerified = true;
            this.verifySending.set(false);
            this.saveChanges();
          } else {
            this.alertService.alert({
              title: 'Error',
              message: 'Invalid verification code!',
              icon: 'error',
              confirmText: 'OK',
              cancelText: 'Cancel',
              type: 'alert',
            }).then((result: any) => {
              if (result) {
                this.verifySending.set(false);
              }
            });
            return;
          }
        });
        return;
      }
    }
  }

  isDarkMode() {
    return this.themeService.isDarkMode();
  }

  toggleDarkMode() {
    this.themeService.toggle();
  }

  async exitImpersonation(): Promise<void> {
    await this.firebaseService.stopImpersonating();
  }

  saveEmail(): void {
    const uid = this.firebaseService.userId;
    if (!uid || !this.canSaveEmail()) {
      return;
    }

    const email = (this.personalInformationModel().email ?? '').trim().toLowerCase();
    this.emailSavingState.set(true);
    this.emailJustUpdated.set(false);

    this.subscriptionList.add(
      this.userService.updateEmailAsAdmin(uid, email).subscribe({
        next: (result) => {
          this.savedEmail.set(result.firebaseEmail);
          this.personalInformationModel.update((model) => ({ ...model, email: result.firebaseEmail }));
          const shoutUser = this.currentUserService.shoutUser;
          if (shoutUser) {
            this.currentUserService.setShoutUser({ ...shoutUser, email: result.firebaseEmail });
          }
          this.emailSavingState.set(false);
          this.emailJustUpdated.set(true);
          void this.toastNotificationService.alert({
            title: 'Email updated',
            message: `Updated to ${result.firebaseEmail} in Firebase and the user record.`,
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string } }) => {
          this.emailSavingState.set(false);
          void this.toastNotificationService.alert({
            title: 'Email update failed',
            message: err?.error?.message ?? 'Unable to update email. Please try again.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  sendPasswordReset(): void {
    const uid = this.firebaseService.userId;
    if (!uid || !this.canSendPasswordReset()) {
      return;
    }

    this.passwordResetSendingState.set(true);

    this.subscriptionList.add(
      this.userService.sendPasswordResetAsAdmin(uid).subscribe({
        next: (result) => {
          this.passwordResetSendingState.set(false);
          void this.toastNotificationService.alert({
            title: 'Password reset sent',
            message: `A password reset email was sent to ${result.email}.`,
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string } }) => {
          this.passwordResetSendingState.set(false);
          void this.toastNotificationService.alert({
            title: 'Password reset failed',
            message: err?.error?.message ?? 'Unable to send password reset email.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  logout() {
    this.firebaseService.logout().pipe(take(1)).subscribe();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }
}
